import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { LuPlus, LuX, LuLoader2, LuBuilding2, LuShieldCheck } from 'react-icons/lu';

import styles from './Account.module.scss';

const cx = classNames.bind(styles);

const titles = {
    department: {
        title: 'Danh sách chức vụ',
        empty: 'Chưa có chức vụ nào.',
        placeholder: 'Nhập tên chức vụ',
        addLabel: 'Thêm chức vụ',
    },
    position: {
        title: 'Danh sách phòng ban',
        empty: 'Chưa có phòng ban nào.',
        placeholder: 'Nhập tên phòng ban',
        addLabel: 'Thêm phòng ban',
    },
};

function DirectoryModal({
    open,
    mode,
    items,
    isLoading,
    isCreating,
    // actionError,
    onClose,
    onCreate,
}) {
    const [isAdding, setIsAdding] = useState(false);
    const [value, setValue] = useState('');
    const [localError, setLocalError] = useState('');

    const config = useMemo(() => titles[mode] ?? titles.department, [mode]);

    useEffect(() => {
        if (!open) {
            setIsAdding(false);
            setValue('');
            setLocalError('');
        }
    }, [open, mode]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKey = (event) => {
            if (event.key === 'Escape' && !isCreating) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [open, isCreating, onClose]);

    if (!open) return null;

    const handleBackdropMouseDown = () => {
        if (isCreating) return;
        onClose();
    };

    const startAdding = () => {
        setIsAdding(true);
        setValue('');
        setLocalError('');
    };

    const cancelAdding = () => {
        if (isCreating) return;
        setIsAdding(false);
        setValue('');
        setLocalError('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const trimmed = value.trim();
        if (!trimmed) {
            setLocalError('Vui lòng nhập tên.');
            return;
        }
        if (typeof onCreate === 'function') {
            const success = await onCreate(trimmed);
            if (success) {
                setIsAdding(false);
                setValue('');
                setLocalError('');
            } else {
                setLocalError('Không thể thêm mới, vui lòng thử lại.');
            }
        }
    };

    // const combinedError = localError || actionError;

    return (
        <div className={cx('modal-backdrop')} role="presentation" onMouseDown={handleBackdropMouseDown}>
            <div
                className={cx('modal', 'directory-modal')}
                role="dialog"
                aria-modal="true"
                aria-labelledby="directory-modal-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header className={cx('modal__header')}>
                    <div>
                        <h2 id="directory-modal-title">{config.title}</h2>
                        <p>Quản lý {mode === 'department' ? 'chức vụ' : 'phòng ban'} trong hệ thống.</p>
                    </div>
                    <button type="button" className={cx('modal__close')} onClick={handleBackdropMouseDown} disabled={isCreating}>
                        <LuX />
                    </button>
                </header>
                <div className={cx('modal__body')}>
                    <div className={cx('directory-toolbar')}>
                        <button type="button" className={cx('primary-button')} onClick={startAdding} disabled={isAdding}>
                            <LuPlus /> {config.addLabel}
                        </button>
                    </div>

                    {isLoading ? (
                        <div className={cx('loading-state')}>Đang tải dữ liệu...</div>
                    ) : (items && items.length > 0 ? (
                        <ul className={cx('directory-list')}>
                            {items.map((item) => (
                                <li key={item._id || item.name} className={cx('directory-item')}>
                                    <span className={cx('directory-item__icon')}>
                                        {mode === 'department' ? <LuBuilding2 /> : <LuShieldCheck />}
                                    </span>
                                    <div className={cx('directory-item__content')}>
                                        <p className={cx('directory-item__name')}>{item.name || 'Không tên'}</p>
                                        {item.description && (
                                            <span className={cx('directory-item__description')}>{item.description}</span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className={cx('directory-empty')}>{config.empty}</div>
                    ))}

                    {isAdding && (
                        <form className={cx('directory-add')} onSubmit={handleSubmit}>
                            <input
                                type="text"
                                autoFocus
                                value={value}
                                placeholder={config.placeholder}
                                onChange={(event) => {
                                    setValue(event.target.value);
                                    setLocalError('');
                                }}
                            />
                            <div className={cx('directory-add__actions')}>
                                <button type="button" className={cx('secondary-button')} onClick={cancelAdding} disabled={isCreating}>
                                    Hủy
                                </button>
                                <button type="submit" className={cx('primary-button', 'primary-button--solid')} disabled={isCreating}>
                                    {isCreating ? <LuLoader2 className={cx('spinning')} /> : 'Lưu'}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* {combinedError && <p className={cx('submit-error')}>{combinedError}</p>} */}
                </div>
            </div>
        </div>
    );
}

export default DirectoryModal;
